import React, { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DateToShortReadableDate } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { UpdatedCover } from '../../../components/renewal/confirmation/updatedCover';
import { SLICES } from '../../../constants/slices';
import { getGtmService } from '../../../services/gtmService/GtmService';
import {
  useGetFaqContentQuery,
  useGetConfirmationPolicyContentQuery,
  useGetCmsTagsQuery
} from '../../../data/kontentAi';
import { BrandContext } from '../../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { FAQ_CODENAME, RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP, DEFAULT_CONTENT } from '../../../constants/cmsCodeName';
import { transformCMSRichText, transformContent } from '../../../utils/transformContent';
import { FaqsBlock } from '../../../components/helpCentre/faqsBlock/faqsBlock';
import './confirmation.scss';

// Presence of any one of these is what distinguishes a confirmed renewal from the cleared {} the
// slice holds otherwise.
const REPORTED_COVER_FIELDS = ['Excess', 'BenefitPercentage', 'RoutineCare', 'BoosterCare', 'newPremium'];

export const RenewalConfirmation = () => {
  const navigate = useNavigate();
  const { activeBrandContext } = useContext(BrandContext);
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const { renewalSummary, adjustedSummary } = useSelector((state) => state?.[SLICES.RENEWAL_HUB]);

  // Fields are picked explicitly: renewalSummary also carries the policyholder's name, email,
  // phone, date of birth and address, none of which belong in GA4.
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current || !renewalSummary?.PolicyNumber) return;

    // Back to /renewal clears adjustedSummary to {} and Forward remounts this page, so the ref
    // alone would let a second view fire asserting a cover the customer never chose.
    const hasAdjustedCover = REPORTED_COVER_FIELDS.some((field) => adjustedSummary?.[field] !== undefined);
    if (!hasAdjustedCover) return;

    viewTrackedRef.current = true;
    getGtmService().pushAdjustCoverConfirmationView({
      policyNumber: renewalSummary.PolicyNumber,
      excess: adjustedSummary.Excess,
      benefitPercentage: adjustedSummary.BenefitPercentage,
      // Coerced because UpdatedCover on this very page renders an absent value as "No", so
      // reporting it as absent would contradict what the customer was shown.
      routineCare: !!adjustedSummary.RoutineCare,
      boosterCare: !!adjustedSummary.BoosterCare,
      annualBenefitLimit: renewalSummary.AnnualBenefitLimit,
      newPremium: adjustedSummary.newPremium,
      newPremiumAnnual: adjustedSummary.newPremiumAnnual,
      paymentFrequency: renewalSummary.PaymentFrequency
    });
  }, [renewalSummary, adjustedSummary]);

  const { data: confirmationContent } = useGetConfirmationPolicyContentQuery(
    {
      previewEnabled: isKontentAiPreviewEnabled,
      element: [DEFAULT_CONTENT.CONTENT, DEFAULT_CONTENT.TITLE]
    },
    { skip: !renewalSummary }
  );
  const { data: faqsData } = useGetFaqContentQuery({
    brandPartner: activeBrandContext.brand.key,
    faqCodeName: FAQ_CODENAME.CONFIRMATION,
    previewEnabled: isKontentAiPreviewEnabled
  });
  const { data: brandTags } = useGetCmsTagsQuery({
    brandPartner: activeBrandContext.brand.key,
    previewEnabled: isKontentAiPreviewEnabled
  });

  const cmsTags = {
    brandpartner_phone: brandTags?.brandpartner_phone
  };
  const hasFaqData = faqsData && Object.keys(faqsData).length > 0;
  const confirmationContentData = {
    ...renewalSummary,
    policyStartDate: DateToShortReadableDate(new Date(renewalSummary?.RenewalStartDate)),
    firstCollectionFee: adjustedSummary?.newPremium,
    firstCollectionDate: DateToShortReadableDate(new Date(renewalSummary?.FirstCollectionDate)),
    ...cmsTags
  };

  const contactMethods = {
    contact_method_post: confirmationContent?.content?.content?.contact_method_post,
    contactmethodemail: confirmationContent?.content?.content?.contactmethodemail
  };

  const handleBackToHome = () => {
    navigate(CSPUrlsMap.dashboard.url);
  };
  const handleBackToMyPets = () => {
    navigate(CSPUrlsMap.myPets.url);
  };

  return (
    <>
      <h1 className="mainPageHeader">
        {transformCMSRichText(
          confirmationContent?.content?.title,
          'Thanks for updating pet cover',
          confirmationContentData,
          RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP
        )}
      </h1>
      <main>
        <div className="confirmationContent">
          {transformContent(
            confirmationContent?.content?.content?.body,
            'What happens next?',
            confirmationContentData,
            RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
            contactMethods
          )}
        </div>
        <UpdatedCover />
        <div className="backButtonContainer">
          <button
            type="button"
            className="button primary"
            onClick={handleBackToHome}
          >
            <span>Back to home</span>
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={handleBackToMyPets}
          >
            <span>Back to My Pets</span>
          </button>
        </div>
      </main>
      <aside>
        {hasFaqData && (
          <FaqsBlock
            questionSet={faqsData?.FAQs?.questions}
            heading={{ text: 'FAQs' }}
            onQuestionToggle={({ question, isOpen }) => {
              // The slice can still be at its initial null here. Dropped rather than pushed without
              // the join key: GTM's merged data layer would leave the previous event's
              // policy_number in place and the tag would report the wrong policy.
              if (!renewalSummary?.PolicyNumber) return;

              getGtmService().pushFaqInteraction({
                policyNumber: renewalSummary.PolicyNumber,
                faqQuestion: question,
                interactionType: isOpen ? 'open' : 'close',
                pageName: 'confirmation'
              });
            }}
          />
        )}
      </aside>
    </>
  );
};
